import { MetricQueryOption } from '@aibees/elephant/feature/search-table-company/hooks/types';
import { IconType } from '@aibees/shared/components';
import { ColorSchema } from '@aibees/shared/theme/palette';
import { INNER_SEPARATOR, ITEM_SEPARATOR } from './config';

export enum EmployeeByDepartmentQueryType {
  INCLUDE = 'INC',
  EXCLUDE = 'EXC',
  INCLUDE_AND = 'INC_AND',
}

export const DEFAULT_QUERY_TYPE = EmployeeByDepartmentQueryType.INCLUDE;

/**
 * Maps each FE query type to the backend `option` field on a metrics row. The
 * FE has 3 query types; the backend defines 4. We don't expose plain EXCLUDE
 * in the UI yet, so the FE EXCLUDE button maps to backend EXCLUDE_AND per
 * product decision.
 */
export const QUERY_TYPE_BACKEND_OPTION: Record<
  EmployeeByDepartmentQueryType,
  MetricQueryOption
> = {
  [EmployeeByDepartmentQueryType.INCLUDE]: 'INCLUDE',
  [EmployeeByDepartmentQueryType.INCLUDE_AND]: 'INCLUDE_AND',
  [EmployeeByDepartmentQueryType.EXCLUDE]: 'EXCLUDE_AND',
};

export interface QueryTypeVisual {
  color: Extract<
    ColorSchema,
    'primary' | 'error' | 'warning' | 'success' | 'info'
  >;
  icon: IconType;
  labelText: string;
  buttonLabel: string;
  /** Section title used in the closed-state card (e.g. `Include (OR)`). */
  sectionTitle: string;
}

export const QUERY_TYPE_VISUAL: Record<
  EmployeeByDepartmentQueryType,
  QueryTypeVisual
> = {
  [EmployeeByDepartmentQueryType.INCLUDE]: {
    color: 'primary',
    icon: 'add-circle-outline',
    labelText: 'Include',
    buttonLabel: 'Include',
    sectionTitle: 'Include (OR)',
  },
  [EmployeeByDepartmentQueryType.EXCLUDE]: {
    color: 'error',
    icon: 'block',
    labelText: 'Exclude',
    buttonLabel: 'Exclude',
    sectionTitle: 'Exclude',
  },
  [EmployeeByDepartmentQueryType.INCLUDE_AND]: {
    color: 'warning',
    icon: 'add-task',
    labelText: 'Include (AND)',
    buttonLabel: 'Include (AND)',
    sectionTitle: 'Include (AND)',
  },
};

const VALID_QUERY_TYPES = new Set<string>(
  Object.values(EmployeeByDepartmentQueryType)
);

function parseQueryType(
  raw: string | undefined
): EmployeeByDepartmentQueryType {
  if (raw && VALID_QUERY_TYPES.has(raw)) {
    return raw as EmployeeByDepartmentQueryType;
  }
  return DEFAULT_QUERY_TYPE;
}

/** Read the queryType for one row. `segmentCount` is the number of input segments (2 for EBD, 3 for Headcount Growth). */
export function getItemQueryType(
  rawRow: string | undefined,
  segmentCount: number
): EmployeeByDepartmentQueryType {
  if (!rawRow) return DEFAULT_QUERY_TYPE;
  const segments = rawRow.split(INNER_SEPARATOR);
  return parseQueryType(segments[segmentCount]);
}

/** Write the queryType for one row, padding missing input segments with empty strings. */
export function setItemQueryType(
  rawRow: string | undefined,
  queryType: EmployeeByDepartmentQueryType,
  segmentCount: number
): string {
  const segments = (rawRow ?? '').split(INNER_SEPARATOR);
  while (segments.length < segmentCount) segments.push('');
  segments[segmentCount] = queryType;
  return segments.join(INNER_SEPARATOR);
}

/** Replace the queryType segment of the row at `index` in the full filter param value. */
export function applyQueryTypeToParam(
  rawParam: string | undefined,
  rowIndex: number,
  queryType: EmployeeByDepartmentQueryType,
  segmentCount: number
): string {
  const rows = (rawParam ?? '').split(ITEM_SEPARATOR);
  while (rows.length <= rowIndex) rows.push('');
  rows[rowIndex] = setItemQueryType(rows[rowIndex], queryType, segmentCount);
  return rows.join(ITEM_SEPARATOR);
}

/**
 * Group rows by queryType for backend dispatch. Returned arrays preserve row order.
 * Each row in the result is the raw row string with the queryType segment removed.
 */
export function groupRowsByQueryType(
  rawParam: string | undefined,
  segmentCount: number
): Record<EmployeeByDepartmentQueryType, string[]> {
  const grouped: Record<EmployeeByDepartmentQueryType, string[]> = {
    [EmployeeByDepartmentQueryType.INCLUDE]: [],
    [EmployeeByDepartmentQueryType.EXCLUDE]: [],
    [EmployeeByDepartmentQueryType.INCLUDE_AND]: [],
  };
  if (!rawParam) return grouped;
  const rows = rawParam.split(ITEM_SEPARATOR);
  for (const row of rows) {
    const qt = getItemQueryType(row, segmentCount);
    const segments = row.split(INNER_SEPARATOR).slice(0, segmentCount);
    grouped[qt].push(segments.join(INNER_SEPARATOR));
  }
  return grouped;
}
